import fieldDataSource from "app/components/common/editor/control/select/field-datasource";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import UIControl from "../../../editor/control/ui-control";
import { ITransformationConfiguration } from "../cq";
import { TransformationUtils } from "./transformation.utils";

const Transformation: ITransformationConfiguration = {
    OnlyDbSources: false,
    allowChangeSource: true
};

const supportedFieldTypes = [
    "java.lang.double",
    "java.lang.float",
    "java.lang.integer",
    "java.lang.long",
    "java.lang.short",
    "org.joda.time.datetime"
];

Transformation.createColumnControl = function() {
    const self = this;
    // We're not actually using the column control for field selection
    // Instead, we'll handle the field selection in the TQL builder
    return UIControl.ColumnsPicker.extend({
        hideLabel: true,
        hidden: true, // Hide the column picker completely
        transformationConfig: {
            preFillColumns: false,
            addAvailable: false,
            autoFillAlias: true,
            hideRemoveIcon: true,
            allowClearColumns: false
        }
    }).create();
};
const getConfigValues = (options: any) => {
    return {
        valueField: options.StaticControls.valueField.getValue(),
        timestampField: options.StaticControls.timestampField.getValue(),
        numStepsToPredict: options.StaticControls.numStepsToPredict.getValue() || "2",
        intervalSeconds: options.StaticControls.intervalSeconds.getValue() || "60",
        algorithmOption: options.StaticControls.algorithmOption.getValue(),
        enableSeasonality: options.StaticControls.enableSeasonality.getValue(),
        seasonalLength: options.StaticControls.seasonalLength.getValue() || "1440",
        detectAnomaly: options.StaticControls.detectAnomaly.getValue(),
        anomalyEpsilon: options.StaticControls.anomalyEpsilon.getValue() ?? "0.05",
        anomalyStart: options.StaticControls.anomalyStart.getValue() || "0",
        anomalyLength: options.StaticControls.anomalyLength.getValue() || "1000",
        retrainFrequency: options.StaticControls.retrainFrequency.getValue() || "1",
        missingValueOption: options.StaticControls.missingValueOption.getValue()
    };
};

const getForeseerTqlBuilder = (stream: string, columns: any[], options: any, transformation: any) => ({
    build() {
        if (!stream) {
            return "";
        }

        const config = getConfigValues(options);

        if (!config.valueField) {
            return "Value for Select is required";
        }

        // Save all values to metadata_object.uiconfig.config
        if (transformation && transformation.metadata_object && transformation.metadata_object.uiconfig) {
            transformation.metadata_object.uiconfig.config = transformation.metadata_object.uiconfig.config || {};
            Object.assign(transformation.metadata_object.uiconfig.config, config);
        }

        // Build the options JSON for makeJson
        let optionsJson = "{";
        optionsJson += `\\\"numStepsToPredict\\\": ${config.numStepsToPredict}`;
        optionsJson += `, \\\"retrainFrequency\\\": ${config.retrainFrequency}`;
        optionsJson += `, \\\"detectAnomaly\\\": ${config.detectAnomaly}`;

        if (config.detectAnomaly) {
            optionsJson += `, \\\"anomalyEpsilon\\\": ${config.anomalyEpsilon}`;
            optionsJson += `, \\\"anomalyStart\\\": ${config.anomalyStart}`;
            optionsJson += `, \\\"anomalyLength\\\": ${config.anomalyLength}`;
        }

        optionsJson += `, \\\"enableSeasonality\\\": ${config.enableSeasonality}`;

        if (config.enableSeasonality) {
            optionsJson += `, \\\"seasonalLength\\\": ${config.seasonalLength}`;
        }

        optionsJson += `, \\\"missingValueOption\\\": \\\"${config.missingValueOption}\\\"`;
        optionsJson += `, \\\"algorithmOption\\\": \\\"${config.algorithmOption}\\\"`;
        optionsJson += "}";

        // Build the TQL
        let tql = "SELECT ";

        // Add the forecast function call with proper syntax - output the forecast object directly
        if (config.timestampField) {
            tql += `forecast(${config.valueField}, ${config.timestampField}, ${config.intervalSeconds}, makeJson("${optionsJson}"))\n`;
        } else {
            tql += `forecast(${config.valueField}, null, ${config.intervalSeconds}, makeJson("${optionsJson}"))\n`;
        }

        // Add FROM clause
        tql += `FROM ${stream}`;

        // Store the query in the metadata
        if (transformation && transformation.metadata_object && transformation.metadata_object.uiconfig) {
            transformation.metadata_object.uiconfig.query = tql;
        }

        return tql;
    }
});

Transformation.createRqlBuilder = getForeseerTqlBuilder;

function getControlRow(control) {
    return control.$el.closest(".field-row");
}

Transformation.convertToCQ = function() {
    // Hide all controls when converting to CQ
    getControlRow(this.options.StaticControls.numStepsToPredict).hide();
    getControlRow(this.options.StaticControls.intervalSeconds).hide();
    getControlRow(this.options.StaticControls.algorithmOption).hide();
    getControlRow(this.options.StaticControls.enableSeasonality).hide();
    getControlRow(this.options.StaticControls.seasonalLength).hide();
    getControlRow(this.options.StaticControls.detectAnomaly).hide();
    getControlRow(this.options.StaticControls.anomalyEpsilon).hide();
    getControlRow(this.options.StaticControls.anomalyStart).hide();
    getControlRow(this.options.StaticControls.anomalyLength).hide();
    getControlRow(this.options.StaticControls.retrainFrequency).hide();
    getControlRow(this.options.StaticControls.missingValueOption).hide();
};

Transformation.buildTql = function() {};

Transformation.postProcessing = function(cqControl: any, options: any, metadata_object: any) {
    // Initialize form's dirty state to false
    if (cqControl.Form && cqControl.Form.model) {
        // Explicitly set isDirty to false and clear dirtyControls array
        cqControl.Form.model.isDirty = false;
        cqControl.Form.model.dirtyControls = [];
        cqControl.Form.model.refreshDirty();

        // Force the ActionsView to update its UI state
        if (cqControl.Form.view && cqControl.Form.view.actionsView) {
            cqControl.Form.view.actionsView.onDirtyChange();
        }

        // Listen for the form's render event to ensure the Save button is properly disabled
        cqControl.Form.on("form:rendered", function() {
            setTimeout(function() {
                if (cqControl.Form && cqControl.Form.model) {
                    cqControl.Form.model.isDirty = false;
                    cqControl.Form.model.dirtyControls = [];
                    cqControl.Form.model.refreshDirty();

                    if (cqControl.Form.view && cqControl.Form.view.actionsView) {
                        cqControl.Form.view.actionsView.onDirtyChange();
                        cqControl.Form.view.actionsView.disabled = true;
                        cqControl.Form.view.actionsView.ui.btnOk.addClass("disabled");
                    }
                }
            }, 100);
        });
    }

    // Set up input change handler to load fields when input changes
    cqControl.input.on(UIControl.EVENT.VALUE_CHANGED, function(inputValue) {
        if (!inputValue) {
            options.StaticControls.valueField.setValue("");
            options.StaticControls.valueField.setDataSource([]);
            options.StaticControls.timestampField.setValue("");
            options.StaticControls.timestampField.setDataSource([]);
            cqControl.refreshTql();
            return;
        }
        options.StaticControls.valueField.setValue("");
        options.StaticControls.timestampField.setValue("");

        // Mark form as dirty when input changes
        if (cqControl.Form && cqControl.Form.model) {
            cqControl.Form.model.isDirty = true;
            cqControl.Form.model.refreshDirty();
        }

        metaStoreService.findById(inputValue).then(function(componentModel) {
            if (!componentModel) {
                return;
            }
            var fieldName =
                componentModel.type === metaStoreService.entities.CACHE
                    ? componentModel.typename
                    : componentModel.dataType;

            fieldDataSource(fieldName).then(fields => {
                // Update field selectors with all available fields
                if (options.StaticControls.valueField) {
                    options.StaticControls.valueField.setDataSource(fields);
                }
                if (options.StaticControls.timestampField) {
                    options.StaticControls.timestampField.setDataSource(fields);
                }
            });
        });
    });

    // Initial load of fields if input is already set
    const inputValue = cqControl.input.getValue();
    if (inputValue) {
        metaStoreService.findById(inputValue).then(function(componentModel) {
            if (!componentModel) {
                return;
            }
            var fieldName =
                componentModel.type === metaStoreService.entities.CACHE
                    ? componentModel.typename
                    : componentModel.dataType;

            fieldDataSource(fieldName).then(fields => {
                // Update field selectors with all available fields
                if (options.StaticControls.valueField) {
                    options.StaticControls.valueField.setDataSource(fields);
                }
                if (options.StaticControls.timestampField) {
                    options.StaticControls.timestampField.setDataSource(fields);
                }
            });
        });
    }

    // add additional fields
    const index = options.displayFields.indexOf("convertToCQ");

    // First add the input field right after the name field (which is at index 0)
    // The input field is already in the displayFields at index 1

    // Then add the value and timestamp fields
    options.displayFields.splice(2, 0, "valueField");
    options.displayFields.splice(3, 0, "timestampField");

    // Add the rest of the fields
    options.displayFields.splice(index + 2, 0, "numStepsToPredict");
    options.displayFields.splice(index + 3, 0, "intervalSeconds");
    options.displayFields.splice(index + 4, 0, "algorithmOption");
    options.displayFields.splice(index + 5, 0, "enableSeasonality");
    options.displayFields.splice(index + 6, 0, "seasonalLength");
    options.displayFields.splice(index + 7, 0, "detectAnomaly");
    options.displayFields.splice(index + 8, 0, "anomalyEpsilon");
    options.displayFields.splice(index + 9, 0, "anomalyStart");
    options.displayFields.splice(index + 10, 0, "anomalyLength");
    options.displayFields.splice(index + 11, 0, "retrainFrequency");
    options.displayFields.splice(index + 12, 0, "missingValueOption");

    // Create field selection controls with custom templates to show field types
    options.StaticControls.valueField = UIControl.Select([], {
        label: "Value Field",
        required: true,
        placeholder: "Select field",
        templateResult: function(item: any) {
            if (!item.id) return item.text;
            return $("<span>" + item.text + " <small style='color:#999'>(" + item.description + ")</small></span>");
        },
        templateSelection: function(item: any) {
            if (!item.id) return item.text;
            return $("<span>" + item.text + " <small style='color:#999'>(" + item.description + ")</small></span>");
        },
        onChange: (selectedField: string) => {
            metadata_object.uiconfig.config.valueField = selectedField;
            cqControl.refreshTql();
            // Mark form as dirty
            if (cqControl.Form && cqControl.Form.model) {
                cqControl.Form.model.isDirty = true;
                cqControl.Form.model.refreshDirty();
            }
        }
    }).create();

    options.StaticControls.timestampField = UIControl.Select([], {
        label: "Timestamp Field",
        required: false,
        placeholder: "Select field (optional)",
        templateResult: function(item: any) {
            if (!item.id) return item.text;
            return $("<span>" + item.text + " <small style='color:#999'>(" + item.description + ")</small></span>");
        },
        templateSelection: function(item: any) {
            if (!item.id) return item.text;
            return $("<span>" + item.text + " <small style='color:#999'>(" + item.description + ")</small></span>");
        },
        onChange: (selectedField: string) => {
            metadata_object.uiconfig.config.timestampField = selectedField;
            cqControl.refreshTql();
            // Mark form as dirty
            if (cqControl.Form && cqControl.Form.model) {
                cqControl.Form.model.isDirty = true;
                cqControl.Form.model.refreshDirty();
            }
        }
    }).create();

    // create other controls
    options.StaticControls.numStepsToPredict = UIControl.PositiveNumberField.extend({
        defaultValue: "2",
        label: "Num Steps To Predict"
    }).create();
    options.StaticControls.numStepsToPredict.setValue("2");

    options.StaticControls.intervalSeconds = UIControl.PositiveNumberField.extend({
        defaultValue: "60",
        label: "Interval Seconds"
    }).create();
    options.StaticControls.intervalSeconds.setValue("60");

    const algorithmOptionSource = [
        { id: "arima", text: "ARIMA" },
        { id: "randomForest", text: "Random Forest" }
    ];
    options.StaticControls.algorithmOption = UIControl.Select(algorithmOptionSource, {
        hideSearch: true,
        allowClear: false,
        label: "Algorithm Option"
    }).create();
    options.StaticControls.algorithmOption.setValue("arima");

    options.StaticControls.enableSeasonality = UIControl.Checkbox.extend({
        label: "Enable Seasonality"
    }).create();
    options.StaticControls.enableSeasonality.setValue(false);

    options.StaticControls.seasonalLength = UIControl.PositiveNumberField.extend({
        defaultValue: "1440",
        label: "Seasonal Length"
    }).create();
    options.StaticControls.seasonalLength.setValue("1440");

    options.StaticControls.detectAnomaly = UIControl.Checkbox.extend({
        label: "Detect Anomaly"
    }).create();
    options.StaticControls.detectAnomaly.setValue(false);

    options.StaticControls.anomalyEpsilon = UIControl.FloatField.extend({
        defaultValue: "0.05",
        label: "Anomaly Epsilon",
        min: 0,
        max: 1
    }).create();
    options.StaticControls.anomalyEpsilon.setValue("0.05");

    options.StaticControls.anomalyStart = UIControl.PositiveNumberField.extend({
        defaultValue: "0",
        label: "Anomaly Start"
    }).create();
    options.StaticControls.anomalyStart.setValue("0");

    options.StaticControls.anomalyLength = UIControl.PositiveNumberField.extend({
        defaultValue: "1000",
        label: "Anomaly Length"
    }).create();
    options.StaticControls.anomalyLength.setValue("1000");

    options.StaticControls.retrainFrequency = UIControl.PositiveNumberField.extend({
        defaultValue: "1",
        label: "Retrain Frequency"
    }).create();
    options.StaticControls.retrainFrequency.setValue("1");

    const missingValueOptionSource = [
        { id: "ignore", text: "Ignore" },
        { id: "zero", text: "Zero" }
    ];
    options.StaticControls.missingValueOption = UIControl.Select(missingValueOptionSource, {
        hideSearch: true,
        allowClear: false,
        label: "Missing Value Option"
    }).create();
    options.StaticControls.missingValueOption.setValue("ignore");

    // initialize control values from metadata if available
    if (metadata_object?.uiconfig?.config) {
        if (metadata_object?.uiconfig?.config.valueField) {
            options.StaticControls.valueField.setValue(metadata_object?.uiconfig?.config.valueField);
        }
        if (metadata_object?.uiconfig?.config.timestampField) {
            options.StaticControls.timestampField.setValue(metadata_object?.uiconfig?.config.timestampField);
        }
        if (metadata_object?.uiconfig?.config.numStepsToPredict) {
            options.StaticControls.numStepsToPredict.setValue(metadata_object?.uiconfig?.config.numStepsToPredict);
        }
        if (metadata_object?.uiconfig?.config.intervalSeconds) {
            options.StaticControls.intervalSeconds.setValue(metadata_object?.uiconfig?.config.intervalSeconds);
        }
        if (metadata_object?.uiconfig?.config.algorithmOption) {
            options.StaticControls.algorithmOption.setValue(metadata_object?.uiconfig?.config.algorithmOption);
        }
        if (metadata_object?.uiconfig?.config.enableSeasonality !== undefined) {
            options.StaticControls.enableSeasonality.setValue(metadata_object?.uiconfig?.config.enableSeasonality);
        }
        if (metadata_object?.uiconfig?.config.seasonalLength) {
            options.StaticControls.seasonalLength.setValue(metadata_object?.uiconfig?.config.seasonalLength);
        }
        if (metadata_object?.uiconfig?.config.detectAnomaly !== undefined) {
            options.StaticControls.detectAnomaly.setValue(metadata_object?.uiconfig?.config.detectAnomaly);
        }
        if (metadata_object?.uiconfig?.config.anomalyEpsilon) {
            options.StaticControls.anomalyEpsilon.setValue(metadata_object?.uiconfig?.config.anomalyEpsilon);
        }
        if (metadata_object?.uiconfig?.config.anomalyStart) {
            options.StaticControls.anomalyStart.setValue(metadata_object?.uiconfig?.config.anomalyStart);
        }
        if (metadata_object?.uiconfig?.config.anomalyLength) {
            options.StaticControls.anomalyLength.setValue(metadata_object?.uiconfig?.config.anomalyLength);
        }
        if (metadata_object?.uiconfig?.config.retrainFrequency) {
            options.StaticControls.retrainFrequency.setValue(metadata_object?.uiconfig?.config.retrainFrequency);
        }
        if (metadata_object?.uiconfig?.config.missingValueOption) {
            options.StaticControls.missingValueOption.setValue(metadata_object?.uiconfig?.config.missingValueOption);
        }
    }

    // Common function to handle value changes
    const handleValueChange = (controlName, value) => {
        metadata_object.uiconfig.config[controlName] = value;
        cqControl.refreshTql();
        // Mark form as dirty
        if (cqControl.Form && cqControl.Form.model) {
            cqControl.Form.model.isDirty = true;
            cqControl.Form.model.refreshDirty();
        }
    };

    // Set up event handlers for control value changes
    options.StaticControls.valueField.on(UIControl.EVENT.VALUE_CHANGED, value => {
        options.StaticControls.valueField.clearError();
        handleValueChange("valueField", value);
    });

    options.StaticControls.timestampField.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("timestampField", value);
    });

    options.StaticControls.numStepsToPredict.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("numStepsToPredict", value);
    });

    options.StaticControls.intervalSeconds.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("intervalSeconds", value);
    });

    options.StaticControls.algorithmOption.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("algorithmOption", value);
    });

    options.StaticControls.enableSeasonality.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("enableSeasonality", value);
        toggleSeasonalityControls(value);
    });

    options.StaticControls.seasonalLength.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("seasonalLength", value);
    });

    options.StaticControls.detectAnomaly.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("detectAnomaly", value);
        toggleAnomalyControls(value);
    });

    options.StaticControls.anomalyEpsilon.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("anomalyEpsilon", value);
    });

    options.StaticControls.anomalyStart.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("anomalyStart", value);
    });

    options.StaticControls.anomalyLength.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("anomalyLength", value);
    });

    options.StaticControls.retrainFrequency.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("retrainFrequency", value);
    });

    options.StaticControls.missingValueOption.on(UIControl.EVENT.VALUE_CHANGED, value => {
        handleValueChange("missingValueOption", value);
    });

    // Function to toggle seasonality-related controls
    function toggleSeasonalityControls(enableSeasonality) {
        if (enableSeasonality) {
            getControlRow(options.StaticControls.seasonalLength).show();
        } else {
            getControlRow(options.StaticControls.seasonalLength).hide();
        }
    }

    // Function to toggle anomaly-related controls
    function toggleAnomalyControls(detectAnomaly) {
        if (detectAnomaly) {
            getControlRow(options.StaticControls.anomalyEpsilon).show();
            getControlRow(options.StaticControls.anomalyStart).show();
            getControlRow(options.StaticControls.anomalyLength).show();
        } else {
            getControlRow(options.StaticControls.anomalyEpsilon).hide();
            getControlRow(options.StaticControls.anomalyStart).hide();
            getControlRow(options.StaticControls.anomalyLength).hide();
        }
    }

    // Override refreshTql to ensure the query field is updated
    const originalRefreshTql = cqControl.refreshTql;
    cqControl.refreshTql = function() {
        // Call the original refreshTql method
        originalRefreshTql.call(cqControl);

        const config = getConfigValues(options);

        if (!config.valueField) {
            return;
        }

        // Save all values to metadata_object.uiconfig.config
        metadata_object.uiconfig.config = metadata_object.uiconfig.config || {};
        Object.assign(metadata_object.uiconfig.config, config);

        // Build the options JSON for makeJson
        let optionsJson = "{";
        optionsJson += `\\\"numStepsToPredict\\\": ${config.numStepsToPredict}`;
        optionsJson += `, \\\"retrainFrequency\\\": ${config.retrainFrequency}`;
        optionsJson += `, \\\"detectAnomaly\\\": ${config.detectAnomaly}`;

        if (config.detectAnomaly) {
            optionsJson += `, \\\"anomalyEpsilon\\\": ${config.anomalyEpsilon}`;
            optionsJson += `, \\\"anomalyStart\\\": ${config.anomalyStart}`;
            optionsJson += `, \\\"anomalyLength\\\": ${config.anomalyLength}`;
        }

        optionsJson += `, \\\"enableSeasonality\\\": ${config.enableSeasonality}`;

        if (config.enableSeasonality) {
            optionsJson += `, \\\"seasonalLength\\\": ${config.seasonalLength}`;
        }

        optionsJson += `, \\\"missingValueOption\\\": \\\"${config.missingValueOption}\\\"`;
        optionsJson += `, \\\"algorithmOption\\\": \\\"${config.algorithmOption}\\\"`;
        optionsJson += "}";

        // Build the TQL
        const selectedStreamId = cqControl.input.getValue();
        if (!selectedStreamId) {
            return;
        }

        const stream = TransformationUtils.getInputStreamName(selectedStreamId);

        let tql = "SELECT ";

        // Add the forecast function call with proper syntax - output the forecast object directly
        if (config.timestampField) {
            tql += `forecast(${config.valueField}, ${config.timestampField}, ${config.intervalSeconds}, makeJson("${optionsJson}"))\n`;
        } else {
            tql += `forecast(${config.valueField}, null, ${config.intervalSeconds}, makeJson("${optionsJson}"))\n`;
        }

        // Add FROM clause
        tql += `as forecast FROM ${stream}`;

        // Store the query in the metadata
        metadata_object.uiconfig.query = tql;

        // Directly set the query field
        cqControl.fieldMapper.select.setValue(tql);
    };

    // Initialize control visibility based on current values
    setTimeout(function() {
        toggleSeasonalityControls(options.StaticControls.enableSeasonality.getValue());
        toggleAnomalyControls(options.StaticControls.detectAnomaly.getValue());
        cqControl.refreshTql();

        // Reset form's dirty state after initialization
        if (cqControl.Form && cqControl.Form.model) {
            // Explicitly set isDirty to false and clear dirtyControls array
            cqControl.Form.model.isDirty = false;
            cqControl.Form.model.dirtyControls = [];
            cqControl.Form.model.refreshDirty();

            // Force the ActionsView to update its UI state
            if (cqControl.Form.view && cqControl.Form.view.actionsView) {
                cqControl.Form.view.actionsView.onDirtyChange();
                // Explicitly disable the Save button
                cqControl.Form.view.actionsView.disabled = true;
                cqControl.Form.view.actionsView.ui.btnOk.addClass("disabled");
            }
        }
    }, 500);

    // Listen for form submission to reset dirty state after save
    if (cqControl.Form && cqControl.Form.view) {
        cqControl.Form.view.on("submit", function() {
            // Reset dirty state after successful save
            setTimeout(function() {
                if (cqControl.Form && cqControl.Form.model) {
                    // Explicitly set isDirty to false and clear dirtyControls array
                    cqControl.Form.model.isDirty = false;
                    cqControl.Form.model.dirtyControls = [];
                    cqControl.Form.model.refreshDirty();

                    // Force the ActionsView to update its UI state
                    if (cqControl.Form.view && cqControl.Form.view.actionsView) {
                        cqControl.Form.view.actionsView.onDirtyChange();
                        // Explicitly disable the Save button
                        cqControl.Form.view.actionsView.disabled = true;
                        cqControl.Form.view.actionsView.ui.btnOk.addClass("disabled");
                    }
                }
            }, 100);
        });
    }
};

Transformation.postRender = function (cqControl: any) {
    cqControl.Form.on("form-builder:save-click", function () {
        const value = cqControl.options.StaticControls.valueField.getValue();
        if (!value) {
            cqControl.options.StaticControls.valueField.showError("Value is required")
        }
    });
};

export default Transformation;
